
import React, {
  useState,
  useEffect,
  Fragment,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { IconSearch } from '@tabler/icons-react'
import MovieListSearch from 'components/MovieListSearch'

// Services
import { getBlueFoxCanadaSanityData } from 'services/MovieApi'

// Interfaces
import { IMovie } from 'interfaces/IMovie'

type ModalSearchHandle = { openModal: () => void }
type Props = { children?: React.ReactNode | null }

const includesCI = (hay: unknown, needle: string) => {
  const h =
    typeof hay === 'string'
      ? hay
      : (hay as any)?.current && typeof (hay as any).current === 'string'
        ? (hay as any).current
        : ''
  return h.toLowerCase().includes(needle.toLowerCase().trim())
}

const useDebouncedValue = (value: string, delay = 200) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

const ModalSearch = forwardRef<ModalSearchHandle, Props>((_props, ref) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [recentSearch, setRecentSearch] = useState<IMovie[]>([])
  const [valueSearch, setValueSearch] = useState('')
  const [searchList, setSearchList] = useState<IMovie[]>([])
  const [allMovies, setAllMovies] = useState<any[]>([])
  const [loadingAll, setLoadingAll] = useState(false)

  const debouncedQuery = useDebouncedValue(valueSearch, 200)
  const fetchedOnceRef = useRef(false)

  const openModal = () => setIsOpen(true)
  const closeModal = () => {
    setIsOpen(false)
    setSearchList([])
    setValueSearch('')
  }

  useImperativeHandle(ref, () => ({ openModal }), [])

  // Load “recent” when opened
  useEffect(() => {
    if (isOpen) {
      const recents = localStorage.getItem('recent-search')
      if (recents) setRecentSearch(JSON.parse(recents))
    }
  }, [isOpen])

  // Fetch Sanity list once (when modal first opens)
  useEffect(() => {
    if (!isOpen || fetchedOnceRef.current) return
    ;(async () => {
      try {
        setLoadingAll(true)
        const { data } = await getBlueFoxCanadaSanityData()
        const items = data?.results ?? data?.items ?? []
        setAllMovies(items)
        fetchedOnceRef.current = true
      } catch (err) {
        console.error('Sanity fetch failed:', err)
        setAllMovies([])
      } finally {
        setLoadingAll(false)
      }
    })()
  }, [isOpen])

  // Filter locally with debounce
  useEffect(() => {
    const q = debouncedQuery.trim()
    if (q.length < 2 || !allMovies.length) {
      setSearchList(q ? [] : []) // no results if typing less than 2 chars
      return
    }

    const filtered = allMovies
      .filter((m: any) => includesCI(m.title, q) || includesCI(m.slug, q))
      .sort((a: any, b: any) => {
        const qa = q.toLowerCase()
        const aTitle = (a.title ?? '').toLowerCase()
        const bTitle = (b.title ?? '').toLowerCase()
        const aStarts = aTitle.startsWith(qa) ? -1 : 0
        const bStarts = bTitle.startsWith(qa) ? -1 : 0
        return aStarts - bStarts
      })
      .slice(0, 8)
      .map(
        (m: any): IMovie => ({
          id: m.id ?? m.tmdbId ?? m._id,
          title: m.title,
          name: m.title,
          poster_path: typeof m.poster_path === 'string' ? m.poster_path : '', // full Sanity URL from your normalizer
          release_date: m.release_date ?? m.releaseDate ?? '',
          // keep slug as string if present
          // MovieListSearch doesn’t need it, but we use it for navigation:
          slug: typeof m.slug === 'string' ? m.slug : m.slug?.current,
          vote_average: m.vote_average ?? 0,
          vote_count: m.vote_count ?? 0,
          media_type: 'movie',
          backdrop_path: '',
          genre_ids: [],
          original_language: '',
          original_title: '',
          overview: '',
          popularity: '',
        }),
      )

    setSearchList(filtered)
  }, [debouncedQuery, allMovies])
  // console.log(searchList)

  // Click result
  const handleClick = (item: IMovie, isRecent: boolean) => {
    if (!isRecent) {
      // de-dup recent by id
      const next = [...recentSearch.filter((r) => r.id !== item.id), item].slice(-10)
      setRecentSearch(next)
      localStorage.setItem('recent-search', JSON.stringify(next))
    }
    const slugOrId = (item as any).slug || item.id
    navigate(`/movie/detail/${slugOrId}`)
    closeModal()
  }

  // Clear “recent”
  const handleClearRecent = () => {
    setRecentSearch([])
    localStorage.removeItem('recent-search')
  }

  // Derived flags
  const showRecent = useMemo(
    () => recentSearch.length > 0 && !valueSearch,
    [recentSearch, valueSearch],
  )
  const showNoRecent = useMemo(
    () => recentSearch.length === 0 && !valueSearch,
    [recentSearch, valueSearch],
  )
  const showResults = useMemo(
    () => Boolean(valueSearch) && searchList.length > 0,
    [valueSearch, searchList],
  )
  const showNoResults = useMemo(
    () => Boolean(valueSearch) && searchList.length === 0 && !loadingAll,
    [valueSearch, searchList.length, loadingAll],
  )

  return (
    <Transition
      appear
      show={isOpen}
      as={Fragment}
    >
      <Dialog
        as='div'
        className='relative z-[999999]'
        onClose={closeModal}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/50 backdrop-blur' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md p-4 text-left align-middle rounded-sm bg-background-light dark:bg-slate-900 shadow-xl transform overflow-hidden transition-all'>
                <div className='relative w-full'>
                  <IconSearch
                    size={20}
                    className='absolute left-3 top-[10px] text-slate-600 dark:text-slate-400'
                  />
                  <input
                    type='text'
                    placeholder='Search movie'
                    className='w-full p-2 pl-10 bg-background-light dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-600 placeholder:dark:text-slate-400 border border-gray-400/60 dark:border-slate-700 focus:!border-blue-500 outline-none rounded-sm transition-all duration-300'
                    value={valueSearch}
                    onChange={(e) => setValueSearch(e.target.value)}
                  />
                </div>

                <div className='max-h-[40vh] mt-4 space-y-4 overflow-y-auto scroll-thin'>
                  {showRecent && (
                    <div className='flex flex-col space-y-4'>
                      <div className='flex justify-between'>
                        <p className='font-semibold text-slate-900 dark:text-slate-100'>Recent</p>
                        <button
                          className='font-semibold text-sm text-blue-600 dark:text-blue-400 cursor-pointer'
                          onClick={handleClearRecent}
                        >
                          Clear
                        </button>
                      </div>
                      {recentSearch.map((list) => (
                        <div key={list.id}>
                          <MovieListSearch
                            movie={list}
                            isRecent={true}
                            handleClick={handleClick}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {showNoRecent && (
                    <div className='h-20 flex justify-center items-center text-slate-600 dark:text-slate-400'>
                      No recent searches
                    </div>
                  )}

                  {showResults &&
                    searchList.map((list) => (
                      <div key={list.id}>
                        <MovieListSearch
                          movie={list}
                          handleClick={handleClick}
                        />
                      </div>
                    ))}

                  {showNoResults && (
                    <div className='h-20 flex justify-center items-center text-slate-600 dark:text-slate-400'>
                      No results found
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
})

ModalSearch.displayName = 'ModalSearch'
export default ModalSearch
